import { Container, Graphics, SimpleMesh, Sprite } from "pixi.js"
import { getCurrentTime } from "game-common/models"
import { HatchView, hatchOpenness, hatchHealth, HATCH_MAX_HP, HATCH_BREAK_MS } from "game-common/room/hatch"
import { contentTexture } from "./content_textures"
import { BODY_GROUND_OFFSET } from "game-common/lpc/body_placement"

// The upright ladder has 12px of top padding and a 4px-thick top beam.
// Fasten the underside lid to that beam, rather than into the empty padding above it.
const hingeRow = (underside: boolean): number => underside ? 16 : 12

/** Project the lid around its back hinge. It passes edge-on, then shows its underside. */
export const hatchLidPose = (openness: number): { frontY: number; spread: number; shade: number } => {
    const angle = Math.max(0, Math.min(1, openness)) * Math.PI * .57
    return { frontY: 12 + 28 * Math.cos(angle) - 30 * Math.sin(angle),
        spread: Math.sin(angle) * .75, shade: 1 - Math.sin(angle) * .12 }
}

/** An owned child of the opening sprite, so incremental map repaint also cleans up the lid. */
export class HatchSprite extends Container {
    private panel = new Container()
    private lid: SimpleMesh
    private edge = new Graphics()
    private shadow = new Graphics()
    private cracks = new Graphics()
    private lastOpenness?: number
    constructor(private hatch: HatchView, private underside = false) {
        super()
        this.addChild(this.shadow)
        this.lid = new SimpleMesh(contentTexture("hatch-wood-lid"), new Float32Array(8),
            new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]), new Uint16Array([0, 1, 2, 0, 2, 3]))
        this.panel.addChild(this.lid, this.edge)
        this.addChild(this.panel)
        const hinges = new Graphics()
        const hingeY = hingeRow(underside) - 4
        hinges.beginFill(0x211d1b).drawRect(19, hingeY, 4, 6).drawRect(41, hingeY, 4, 6).endFill()
        hinges.beginFill(0x858079).drawRect(20, hingeY + 1, 2, 2).drawRect(42, hingeY + 1, 2, 2).endFill()
        this.panel.addChild(hinges, this.cracks)
        this.pose()
    }

    pose(now = getCurrentTime()): void {
        if (this.destroyed || this.panel.destroyed) return
        const open = hatchOpenness(this.hatch, now)
        const pose = hatchLidPose(open)
        const angle = open * Math.PI * .57
        const hingeY = hingeRow(this.underside)
        const frontY = this.underside ? hingeY + 10 * Math.cos(angle) - 20 * Math.sin(angle) : pose.frontY
        this.sortPanel(frontY < hingeY, hingeY)
        const hitAge = this.hatch.lastHitAt === undefined ? Infinity : now - this.hatch.lastHitAt
        const shake = hitAge >= 0 && hitAge < 320 ? Math.sin(hitAge * .09) * 1.5 * (1 - hitAge / 320) : 0
        // Offset only the lid artwork; the opening and ladder remain fixed to their floor.
        this.panel.x += shake * this.panel.scale.x
        if (open === this.lastOpenness) return
        this.lastOpenness = open
        const { spread, shade } = pose
        const v = this.lid.vertices
        v[0] = 16; v[1] = hingeY; v[2] = 48; v[3] = hingeY
        v[4] = 48 + spread; v[5] = frontY; v[6] = 16 - spread; v[7] = frontY
        this.lid.tint = Math.round(255 * shade) * 0x010101
        this.shadow.clear().beginFill(0x0c0806, .30 * (1 - open))
            .drawRect(15, hingeY, 34, this.underside ? 11 : 29).endFill()
        this.edge.clear().lineStyle(2, 0x2d1d12, 1)
            .moveTo(16 - spread, frontY).lineTo(48 + spread, frontY)
        const damage = 1 - hatchHealth(this.hatch) / HATCH_MAX_HP
        this.cracks.clear()
        const point = (x: number, t: number) => [x, hingeY + (frontY - hingeY) * t]
        const split = (x: number, start: number, end: number) => {
            const a = point(x, start), b = point(x - 2, (start + end) / 2), c = point(x + 1, end)
            this.cracks.lineStyle(1, 0x25180f, .95).moveTo(a[0], a[1]).lineTo(b[0], b[1]).lineTo(c[0], c[1])
        }
        if (damage > 0) split(28, .18, .55)
        if (damage >= .375) split(39, .42, .95)
        if (damage >= .625) split(22, .6, 1)
    }

    /** Include the independently sorted panel when transferring this floor's owned artwork. */
    depthArtwork = (): Container | undefined => this.panel.destroyed ? undefined : this.panel

    /** The raised door shares the bodies' depth surface; the shaft and shadow stay on the floor. */
    private sortPanel(raised: boolean, hingeY: number): void {
        const opening = this.parent
        if (raised && opening?.parent) {
            const surface = opening.parent
            if (this.panel.parent !== surface) surface.addChild(this.panel)
            this.panel.position.copyFrom(opening.position)
            this.panel.scale.copyFrom(opening.scale)
            this.panel.skew.copyFrom(opening.skew)
            this.panel.rotation = opening.rotation
            // The view from below is attached to a tall ladder: its drawn top is far
            // north of its ground anchor. Keep the lid in that ladder's depth band,
            // or opening it moves it behind the roof the ladder stands in front of.
            this.panel.zIndex = this.underside ? opening.zIndex + .1 :
                100 + opening.y + hingeY * opening.scale.y - BODY_GROUND_OFFSET
            this.panel.visible = opening.visible && this.visible
            this.panel.alpha = opening.alpha * this.alpha
        } else {
            if (this.panel.parent !== this) this.addChild(this.panel)
            this.panel.position.set(0, 0)
            this.panel.scale.set(1, 1)
            this.panel.skew.set(0, 0)
            this.panel.rotation = 0
            this.panel.visible = true
            this.panel.alpha = 1
        }
    }

    destroy(options?: Parameters<Container["destroy"]>[0]): void {
        if (this.destroyed) return
        // The panel can be a sibling of its owning opening when it is raised.
        if (!this.panel.destroyed) {
            this.panel.parent?.removeChild(this.panel)
            this.panel.destroy({ children: true })
        }
        super.destroy(options)
    }
}

export const attachHatchSprite = (opening: Sprite, hatch: HatchView, underside = false): HatchSprite => {
    const sprite = new HatchSprite(hatch, underside)
    opening.addChild(sprite)
    sprite.pose()
    return sprite
}

/** Short-lived lid fragments, owned by the surviving ladder artwork. Never blocks passage. */
export class HatchBreakSprite extends Container {
    private fragments: { mesh: SimpleMesh; x: number; y: number; dx: number; spin: number }[] = []
    private dust = new Graphics()

    constructor(private brokenAt: number, private underside = false) {
        super()
        const height = underside ? 10 : 28, width = 32 / 3
        for (let row = 0; row < 2; row++) for (let col = 0; col < 3; col++) {
            const h = height / 2, u = col / 3, v = row / 2
            const mesh = new SimpleMesh(contentTexture("hatch-wood-lid"),
                new Float32Array([-width / 2, -h / 2, width / 2, -h / 2, width / 2, h / 2, -width / 2, h / 2]),
                new Float32Array([u, v, u + 1 / 3, v, u + 1 / 3, v + .5, u, v + .5]),
                new Uint16Array([0, 1, 2, 0, 2, 3]))
            const sign = (col + row) % 2 ? 1 : -1
            this.fragments.push({ mesh, x: 16 + (col + .5) * width, y: hingeRow(underside) + (row + .5) * h,
                dx: (col - 1) * 15 + sign * (row ? 4 : 7), spin: sign * (row ? 1.4 : .8) })
            this.addChild(mesh)
        }
        this.addChild(this.dust)
    }

    depthArtwork = (): undefined => undefined

    pose(now = getCurrentTime()): void {
        if (this.destroyed) return
        const t = Math.max(0, (now - this.brokenAt) / HATCH_BREAK_MS)
        if (t >= 1) { this.destroy({ children: true }); return }
        const fade = Math.min(1, (1 - t) / .4)
        for (const piece of this.fragments) {
            piece.mesh.position.set(piece.x + piece.dx * t,
                piece.y - 22 * t + (this.underside ? 86 : 54) * t * t)
            piece.mesh.rotation = piece.spin * t
            piece.mesh.scale.set(1 - t * .22)
            piece.mesh.alpha = fade
        }
        this.dust.clear()
        for (let i = 0; i < 10; i++) {
            const dx = (i % 5 - 2) * 9, lift = 10 + i % 3 * 5
            this.dust.beginFill(i % 2 ? 0x9c764a : 0xd1aa70, (1 - t) * .65)
                .drawRect(32 + dx * t, hingeRow(this.underside) + 8 - lift * t + 32 * t * t, 1 + i % 2, 2).endFill()
        }
    }
}

export const attachHatchBreakSprite = (opening: Sprite, brokenAt: number, underside = false): HatchBreakSprite => {
    const sprite = new HatchBreakSprite(brokenAt, underside)
    opening.addChild(sprite)
    sprite.pose()
    return sprite
}
